
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

/**
 * Stage 1: Basics - Experience Creation Wizard
 *
 * Collects core experience information:
 * - Experience name
 * - Category selection (L1 + L2 required, L3 optional)
 * - Brief description
 * - Activity highlights (progressive disclosure after category selection)
 * - Real-time preview card
 *
 * Epic: MVP 1.27 - Experience Creation Wizard
 * Stories: SCRUM-188 to SCRUM-192
 * Location: apps/web/src/components/experiences/creation/stages/Stage1Basics.jsx
 */

import { useState, useEffect, useRef } from 'react';
import FieldWrapper from '../../../common/forms/FieldWrapper';
import ArrayEditor from '../../../common/forms/ArrayEditor';
import FormActions from '../../../common/forms/FormActions';
import HighlightSuggestions from '../../HighlightSuggestions';
import CategoryIcon from '../../../../utils/categoryIcons';

/**
 * Stage1Basics Component
 *
 * @param {Object} props
 * @param {Object} props.formData - Current wizard form data
 * @param {Function} props.updateField - Update single field
 * @param {Function} props.updateFormData - Update multiple fields
 * @param {Function} props.onNext - Navigate to next stage
 * @param {Function} props.onBack - Navigate to previous stage (N/A for stage 1)
 * @param {Object} props.validation - Validation errors for current stage
 * @param {boolean} props.isValid - Whether current stage is valid
 */
export default function Stage1Basics({
  formData,
  updateField,
  updateFormData: _updateFormData,
  onNext,
  onBack: _onBack,
  validation,
  isValid
}) {
  const [newHighlight, setNewHighlight] = useState('');
  const [categories, setCategories] = useState({ l1: [], l2: [], l3: [] });
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryLoadError, setCategoryLoadError] = useState(null);
  const [touchedFields, setTouchedFields] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const highlightInputRef = useRef(null);

  // Extract current values
  const {
    experienceName = '',
    categoryL1Id = null,
    categoryL2Id = null,
    categoryL3Id = null,
    description = '',
    highlights = []
  } = formData;

  /**
   * Mark field as touched (SCRUM-344: validation timing)
   */
  const markFieldTouched = (fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  /**
   * Check if should show validation error (SCRUM-344)
   * Only show if field is touched OR submit was attempted
   */
  const shouldShowError = (fieldName) => {
    return touchedFields[fieldName] || attemptedSubmit;
  };

  /**
   * Handle form submission with validation (SCRUM-344)
   */
  const handleSubmit = () => {
    setAttemptedSubmit(true);
    if (isValid) {
      onNext();
    }
  };

  /**
   * Load Level 1 categories on mount (SCRUM-345: improved error handling)
   */
  useEffect(() => {
    async function loadL1Categories() {
      try {
        setLoadingCategories(true);
        setCategoryLoadError(null);
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/v1/categories/level-1`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {throw new Error('Failed to load categories');}

        const result = await response.json();
        setCategories(prev => ({ ...prev, l1: result.data || [] }));
      } catch (err) {
        console.error('Failed to load L1 categories:', err);
        setCategoryLoadError('Failed to load categories. Please refresh the page.');
      } finally {
        setLoadingCategories(false);
      }
    }

    loadL1Categories();
  }, []);

  /**
   * Load Level 2 categories when L1 changes
   */
  useEffect(() => {
    if (!categoryL1Id) {
      setCategories(prev => ({ ...prev, l2: [], l3: [] }));
      return;
    }

    async function loadL2Categories() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/categories/level-1/${categoryL1Id}/level-2`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {throw new Error('Failed to load L2 categories');}

        const result = await response.json();
        const data = result.data || [];
        setCategories(prev => ({ ...prev, l2: data, l3: [] }));

        // Clear L2 and L3 selections if they're no longer valid
        if (categoryL2Id && !data.find(cat => cat.id === categoryL2Id)) {
          _updateFormData({ categoryL2Id: null, categoryL3Id: null });
        }
      } catch (err) {
        console.error('Failed to load L2 categories:', err);
      }
    }

    loadL2Categories();
  }, [categoryL1Id, _updateFormData, categoryL2Id]);

  /**
   * Load Level 3 categories when L2 changes
   */
  useEffect(() => {
    if (!categoryL2Id) {
      setCategories(prev => ({ ...prev, l3: [] }));
      return;
    }

    async function loadL3Categories() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/categories/level-2/${categoryL2Id}/level-3`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {throw new Error('Failed to load L3 categories');}

        const result = await response.json();
        const data = result.data || [];
        setCategories(prev => ({ ...prev, l3: data }));

        // Clear L3 selection if it's no longer valid
        if (categoryL3Id && !data.find(cat => cat.id === categoryL3Id)) {
          updateField('categoryL3Id', null);
        }
      } catch (err) {
        console.error('Failed to load L3 categories:', err);
      }
    }

    loadL3Categories();
  }, [categoryL2Id, categoryL3Id, updateField]);

  /**
   * Handle template suggestion click (populate input with cursor at __)
   */
  const handleTemplateSuggestion = (text) => {
    setNewHighlight(text);
    highlightInputRef.current?.focus();

    // Position cursor at __ placeholder
    setTimeout(() => {
      const placeholderIndex = text.indexOf('__');
      if (placeholderIndex !== -1 && highlightInputRef.current) {
        highlightInputRef.current.setSelectionRange(placeholderIndex, placeholderIndex + 2);
      }
    }, 0);
  };

  /**
   * Handle complete suggestion click (add directly)
   */
  const handleCompleteSuggestion = (text) => {
    if (highlights.length >= 8) {return;}
    const updated = [...highlights, text];
    updateField('highlights', updated);
  };

  // Show highlights section only after L1 category selected (progressive disclosure)
  const showHighlights = Boolean(categoryL1Id);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Main Form (Left 2 columns) */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Basic Information</h2>

          <div className="space-y-6">
            {/* Experience Name - SCRUM-188, SCRUM-344 */}
            <FieldWrapper
              label="Experience Name"
              htmlFor="experienceName"
              required={true}
              error={shouldShowError('experienceName') ? validation?.errors?.experienceName : null}
              helperText="Choose a descriptive, memorable name (3-100 characters)"
              showCharacterCount={true}
              currentLength={experienceName.length}
              maxLength={100}
            >
              <input
                id="experienceName"
                type="text"
                value={experienceName}
                onChange={(e) => updateField('experienceName', e.target.value)}
                onBlur={() => markFieldTouched('experienceName')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="e.g., Sunset Kayaking in Acadia Bay"
                maxLength={100}
                autoFocus
              />
            </FieldWrapper>

            {/* Category Selection - SCRUM-189, SCRUM-344, SCRUM-345 */}
            <div className="space-y-4">
              {/* L1 Category (Required) - SCRUM-345: Improved visibility and error handling */}
              <FieldWrapper
                label="Primary Category"
                htmlFor="categoryL1"
                required={true}
                error={shouldShowError('categoryL1Id') ? validation?.errors?.categoryL1Id : null}
                helperText="Select the main type of outdoor experience"
              >
                {loadingCategories ? (
                  <div className="text-sm text-gray-500">Loading categories...</div>
                ) : categoryLoadError ? (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{categoryLoadError}</p>
                    <button
                      onClick={() => window.location.reload()}
                      className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                    >
                      Refresh Page
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {categories.l1.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => {
                          // Toggle: if already selected, deselect it
                          const newL1Id = categoryL1Id === category.id ? null : category.id;
                          _updateFormData({
                            categoryL1Id: newL1Id,
                            categoryL2Id: null,
                            categoryL3Id: null
                          });
                          markFieldTouched('categoryL1Id');
                        }}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          categoryL1Id === category.id
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="mb-2 flex justify-center">
                          <CategoryIcon
                            iconName={category.icon_name}
                            size={32}
                            className={categoryL1Id === category.id ? 'text-emerald-600' : 'text-gray-600'}
                          />
                        </div>
                        <div className="text-sm font-medium text-gray-900">{category.name}</div>
                      </button>
                    ))}
                  </div>
                )}
              </FieldWrapper>

              {/* L2 Category (Required, shown after L1 selected) - SCRUM-344 */}
              {categoryL1Id && (
                <FieldWrapper
                  label="Secondary Category"
                  htmlFor="categoryL2"
                  required={true}
                  error={shouldShowError('categoryL2Id') ? validation?.errors?.categoryL2Id : null}
                  helperText="Narrow down your experience type"
                >
                  <select
                    id="categoryL2"
                    value={categoryL2Id || ''}
                    onChange={(  _e) => {
                      _updateFormData({
                        categoryL2Id: parseInt(_e.target.value) || null,
                        categoryL3Id: null
                      });
                      markFieldTouched('categoryL2Id');
                    }}
                    onBlur={() => markFieldTouched('categoryL2Id')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="">Select a category...</option>
                    {categories.l2.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </FieldWrapper>
              )}

              {/* L3 Category (Optional, shown after L2 selected) */}
              {categoryL2Id && categories.l3.length > 0 && (
                <FieldWrapper
                  label="Specific Category"
                  htmlFor="categoryL3"
                  helperText="Optional: Further specify your experience"
                >
                  <select
                    id="categoryL3"
                    value={categoryL3Id || ''}
                    onChange={(e) => updateField('categoryL3Id', parseInt(e.target.value) || null)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="">Select a category (optional)...</option>
                    {categories.l3.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </FieldWrapper>
              )}
            </div>

            {/* Brief Description - SCRUM-190, SCRUM-344 */}
            <FieldWrapper
              label="Brief Description"
              htmlFor="description"
              required={true}
              error={shouldShowError('description') ? validation?.errors?.description : null}
              helperText="Describe what makes this experience special (50-2000 characters)"
              showCharacterCount={true}
              currentLength={description.length}
              maxLength={2000}
            >
              <textarea
                id="description"
                value={description}
                onChange={(e) => updateField('description', e.target.value)}
                onBlur={() => markFieldTouched('description')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-y"
                placeholder="Describe the experience, what guests will see and do, what makes it unique..."
                rows={6}
                minLength={50}
                maxLength={2000}
              />
            </FieldWrapper>

            {/* Activity Highlights - SCRUM-191 (Progressive Disclosure), SCRUM-344 */}
            {showHighlights && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Activity Highlights <span className="text-red-500">*</span>
                  <span className="text-gray-500 font-normal ml-2">(3-8 items required)</span>
                </label>

                <ArrayEditor
                  items={highlights}
                  onChange={(updated) => {
                    updateField('highlights', updated);
                    markFieldTouched('highlights');
                  }}
                  renderItem={(highlight) => (
                    <span className="text-sm text-gray-700">{highlight}</span>
                  )}
                  renderAddForm={({ onAdd }) => (
                    <div>
                      <div className="flex gap-2">
                        <input
                          ref={highlightInputRef}
                          type="text"
                          value={newHighlight}
                          onChange={(e) => setNewHighlight(e.target.value)}
                          onBlur={() => markFieldTouched('highlights')}
                          onKeyPress={(  _e) => {
                            if (_e.key === 'Enter') {
                              _e.preventDefault();
                              if (newHighlight.trim().length >= 3) {
                                onAdd(newHighlight.trim());
                                setNewHighlight('');
                              }
                            }
                          }}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          placeholder="Add a highlight (3-60 characters)"
                          maxLength={60}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (newHighlight.trim().length >= 3) {
                              onAdd(newHighlight.trim());
                              setNewHighlight('');
                              markFieldTouched('highlights');
                            }
                          }}
                          disabled={newHighlight.trim().length < 3}
                          className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                        >
                          Add
                        </button>
                      </div>

                      {/* Highlight Suggestions - MVP 1.20.1-S */}
                      {categoryL1Id && (
                        <HighlightSuggestions
                          categoryL1Id={categoryL1Id}
                          currentHighlights={highlights}
                          onTemplateClick={handleTemplateSuggestion}
                          onCompleteClick={handleCompleteSuggestion}
                        />
                      )}
                    </div>
                  )}
                  maxItems={8}
                  minItems={0}
                  allowReorder={true}
                  showCount={true}
                  emptyMessage="No highlights yet. Add at least 3 highlights to describe what makes this experience special."
                />
                {shouldShowError('highlights') && validation?.errors?.highlights && (
                  <p className="text-sm text-red-600 mt-2">{validation.errors.highlights}</p>
                )}
              </div>
            )}
          </div>

          {/* Form Actions - SCRUM-344: Show validation errors on submit */}
          <FormActions
            onCancel={() => {
              // Stage 1 has no back button (first stage)
            }}
            onSubmit={handleSubmit}
            hasUnsavedChanges={true}
            isSaving={false}
            submitLabel="Continue to Logistics"
            showSaveDraft={false}
            disableCancel={true}
            disableSubmit={false}
            submitVariant="success"
            layout="right"
          />
        </div>
      </div>

      {/* Preview Card - SCRUM-192 (Right column, sticky) */}
      <div className="lg:col-span-1">
        <div className="sticky top-24">
          <ExperiencePreviewCard
            experienceName={experienceName}
            categoryL1Id={categoryL1Id}
            categories={categories}
            description={description}
            highlights={highlights}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Experience Preview Card Component - SCRUM-192
 * Shows real-time preview of customer-facing experience card
 */
function ExperiencePreviewCard({ experienceName, categoryL1Id, categories, description, highlights }) {
  // Find selected category for badge
  const selectedCategory = categories.l1.find(cat => cat.id === categoryL1Id);

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 shadow-md overflow-hidden">
      <div className="bg-gray-100 p-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700">Preview</h3>
        <p className="text-xs text-gray-500">How customers will see your experience</p>
      </div>

      {/* Placeholder Image */}
      <div className="aspect-video bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
        <div className="text-center text-white">
          <svg className="w-16 h-16 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-sm">Cover photo will appear here</p>
        </div>
      </div>

      <div className="p-4">
        {/* Category Badge */}
        {selectedCategory && (
          <div className="mb-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-full">
              <span>{selectedCategory.icon || '🏞️'}</span>
              <span>{selectedCategory.name}</span>
            </span>
          </div>
        )}

        {/* Experience Name */}
        <h4 className="text-lg font-bold text-gray-900 mb-2">
          {experienceName || <span className="text-gray-400 italic">Experience name will appear here</span>}
        </h4>

        {/* Description (Truncated) */}
        <p className="text-sm text-gray-600 mb-3 line-clamp-3">
          {description || <span className="italic text-gray-400">Description will appear here...</span>}
        </p>

        {/* Highlights Preview */}
        {highlights.length > 0 && (
          <div className="space-y-1">
            {highlights.slice(0, 3).map((highlight, index) => (
              <div key={index} className="flex items-start gap-2">
                <span className="text-emerald-600 text-sm mt-0.5">✓</span>
                <span className="text-sm text-gray-700">{highlight}</span>
              </div>
            ))}
            {highlights.length > 3 && (
              <p className="text-xs text-gray-500 mt-2">+{highlights.length - 3} more highlights</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
